import { get } from 'lodash';
import dayjs from 'dayjs';

class TableSorter {
  constructor(
    students,
    resultsByUser,
    studentSessionsByUserId,
    studentResponses,
    itemResultsByUser,
  ) {
    this.studentsItems = students;
    this._unSortedStudent = students.slice(0);
    this.resultsByUser = resultsByUser;
    this.studentSessionsByUserId = studentSessionsByUserId;
    this.responsesByUser = studentResponses;
    this.itemResultsByUser = itemResultsByUser;
  }

  sortStudent(tableSort) {
    const [sortColumn, sortType] = tableSort.split('_');
    this.isAscending = sortType === 'ascending';
    this.isUnsorted = sortType === 'unsorted';
    this.sortColumn = sortColumn;
    /**
     * FY-10855: There should be a secondary sort criterion if there is a tie on the first.
     * It should be name, ascending.
     */
    this.useTieSortOrder = false;

    if (this.isUnsorted) {
      return this._unSortedStudent;
    }
    let sortFn = () => {};
    if (sortColumn === 'name') {
      sortFn = this.sortByStudentName;
    }
    if (sortColumn === 'submitted') {
      sortFn = this.sortBySubmitedDate;
    }
    if (sortColumn === 'pointEarned') {
      sortFn = this.sortByPointEarned;
    }
    if (sortColumn.startsWith('answer-')) {
      const [, learnosityItemReference] = sortColumn.split('-');
      sortFn = this.sortByPartScore(learnosityItemReference);
    }

    if (sortColumn.startsWith('tip-')) {
      const [, questionID] = sortColumn.split('-');
      sortFn = this.sortByIsTipFilled(questionID);
    }

    return this.studentsItems.sort(sortFn.bind(this));
  }

  compare(a, b) {
    const shouldStayBottom = TableSorter.putNullEmptyBelow(a, b);
    if (shouldStayBottom) {
      return shouldStayBottom;
    }
    if (shouldStayBottom === null) {
      return 0;
    }
    const aUpper = a.toUpperCase();
    const bUpper = b.toUpperCase();

    if (aUpper === bUpper) {
      return 0;
    }

    if (this.useTieSortOrder) {
      return aUpper < bUpper ? -1 : 1;
    }

    if (this.isAscending) {
      return aUpper < bUpper ? -1 : 1;
    }
    return aUpper < bUpper ? 1 : -1;
  }

  sortByStudentName(a, b) {
    if (a.lastName.toUpperCase() === b.lastName.toUpperCase()) {
      if (a.firstName.toUpperCase() === b.firstName.toUpperCase()) {
        return 0;
      }
      // sort by firstName if last name are same
      return this.compare(a.firstName, b.firstName);
    }
    return this.compare(a.lastName, b.lastName);
  }

  sortByPointEarned(a, b) {
    let totalPointA = 0;
    let totalPointB = 0;
    const studentAResult = this.resultsByUser[a.id];
    const studentBResult = this.resultsByUser[b.id];

    this.useTieSortOrder = false;

    totalPointA = parseFloat(get(studentAResult, 'score', 0));
    totalPointB = parseFloat(get(studentBResult, 'score', 0));

    if (totalPointA === totalPointB) {
      // sort on student name if tie
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }
    if (this.isAscending) {
      return totalPointA < totalPointB ? -1 : 1;
    }
    return totalPointA < totalPointB ? 1 : -1;
  }

  sortBySubmitedDate(a, b) {
    this.useTieSortOrder = false;
    let s1 = this.studentSessionsByUserId[a.id];
    let s2 = this.studentSessionsByUserId[b.id];

    if (!this.itemResultsByUser[a.id] || !s1.submittedAt) {
      s1 = null;
    }

    if (!this.itemResultsByUser[b.id] || !s2.submittedAt) {
      s2 = null;
    }
    // prevent null or empty values
    const shouldStayBottom = TableSorter.putNullEmptyBelow(s1, s2);
    if (shouldStayBottom) {
      return shouldStayBottom;
    }

    // when there is a No-Result tie
    if (shouldStayBottom === null) {
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }
    const date1 = dayjs(s1.submittedAt);
    const date2 = dayjs(s2.submittedAt);
    const diff = date1.diff(date2);
    if (diff === 0) {
      // sort on student name if tie
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }
    if (diff < 0) {
      return this.isAscending ? -1 : 1;
    }
    return this.isAscending ? 1 : -1;
  }

  sortByPartScore(learnosityItemReference) {
    return (a, b) => {
      this.useTieSortOrder = false;

      let s1 =
        Number.parseInt(
          get(
            this.itemResultsByUser,
            `[${a.id}].items[${learnosityItemReference}].score`,
            0,
          ),
          10,
        ) || 0;
      let s2 =
        Number.parseInt(
          get(
            this.itemResultsByUser,
            `[${b.id}].items[${learnosityItemReference}].score`,
            0,
          ),
          10,
        ) || 0;

      const diff = s1 - s2;
      if (diff === 0) {
        // sort on student name if tie
        this.useTieSortOrder = true;
        return this.sortByStudentName(a, b);
      }
      if (diff < 0) {
        return this.isAscending ? -1 : 1;
      }
      return this.isAscending ? 1 : -1;
    };
  }

  sortByIsTipFilled(questionID) {
    return (a, b) => {
      this.useTieSortOrder = false;

      let s1 = !!get(
        this.responsesByUser,
        `[${a.id}].questions[${questionID}]`,
        0,
      )
        ? 1
        : 0;

      let s2 = !!get(
        this.responsesByUser,
        `[${b.id}].questions[${questionID}]`,
        0,
      )
        ? 1
        : 0;

      const diff = s1 - s2;
      if (diff === 0) {
        // sort on student name if tie
        this.useTieSortOrder = true;
        return this.sortByStudentName(a, b);
      }
      if (diff < 0) {
        return this.isAscending ? -1 : 1;
      }
      return this.isAscending ? 1 : -1;
    };
  }

  static putNullEmptyBelow(a, b) {
    // compare null, undefined for No result tie
    if (!a && !b) {
      return null;
    }
    if (!a) {
      return 1;
    } else if (!b) {
      return -1;
    }
  }
}

export default TableSorter;
